/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { ConfigProvider, Table, Tooltip, theme as antdTheme } from "antd";

import StatusText from "components/Shared/StatusText";
import { getChecksCatalog, getUniverse } from "services/api";
import "./PanelDashboard.scss";


const SEVERITY_RANK = { ok: 0, info: 0, warn: 1, error: 2 };
const DAYS_SINCE_NAME = "DaysSinceLastUpdate";

// Display-only label for a bar size: the raw value ("1Day") stays everywhere that
// matters (routing, filtering, Redis keys, override matching); we only relabel it
// so the end-of-day daily bar reads distinctly from the intraday snapshots.
const formatBarSize = (barSize) => (barSize === "1Day" ? "1Day_EndOfDay" : barSize || "");

const buildSecurityUrl = (row) => {
  if (!row?.securityName) return "/";
  let path = `/${encodeURIComponent(row.securityName)}`;
  if (row.barSize) path += `/${encodeURIComponent(row.barSize)}`;
  return path;
};

const STATUS_FILTER_TYPES = ["OK", "WARN", "ERROR"];
const STATUS_FILTER_COLORS = {
  OK: "#388e3c",
  WARN: "#d6932c",
  ERROR: "#d32f2f",
};

const BASE_COLUMN_TOOLTIPS = {
  securityName: "Ticker / identifier of the security in the dataset.",
  securityType: "Asset class (e.g. Stock, Future, ETF).",
  barSize: "Bar frequency of the stored series (e.g. 1Day, 1Hour).",
  overallSeverity: "Worst severity across all checks — green/VALIDATED once every flag has been reviewed.",
  firstDate: "Timestamp of the earliest bar available for this security.",
  lastDate: "Timestamp of the most recent bar available for this security.",
  description: "Human-readable description of the security.",
  bloombergTicker: "Bloomberg composite ticker for the security.",
};

const BASE_COLUMN_LABELS = {
  securityName: "SecurityName",
  bloombergTicker: "Bloomberg Ticker",
  description: "Description",
  securityType: "Type",
  barSize: "BarSize",
  overallSeverity: "Severity",
  firstDate: "FirstDate",
  lastDate: "LastDate",
};

const DEFAULT_HIDDEN_COLUMNS = new Set(["description", "bloombergTicker", "firstDate", "lastDate"]);
const COLUMN_STORAGE_KEY = "WebDatasetReview.ColumnVisibility.v2";

const readFiltersFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const status = params.get("status");
  const parsedStatus = status
    ? status.split(",").map((s) => s.trim().toUpperCase()).filter((s) => STATUS_FILTER_TYPES.includes(s))
    : null;
  return {
    securityType: params.get("type") || "All",
    barSize: params.get("barSize") || "All",
    minSeverity: params.get("severity") || "All",
    statusFilter: parsedStatus && parsedStatus.length > 0 ? parsedStatus : [...STATUS_FILTER_TYPES],
    search: params.get("search") || "",
  };
};

const buildFilterQuery = ({ securityType, barSize, minSeverity, statusFilter, search }) => {
  const params = new URLSearchParams();
  if (securityType && securityType !== "All") params.set("type", securityType);
  if (barSize && barSize !== "All") params.set("barSize", barSize);
  if (minSeverity && minSeverity !== "All") params.set("severity", minSeverity);
  const isAllStatus =
    statusFilter.length === STATUS_FILTER_TYPES.length &&
    STATUS_FILTER_TYPES.every((t) => statusFilter.includes(t));
  if (!isAllStatus) params.set("status", statusFilter.join(","));
  if (search) params.set("search", search);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const HeaderWithTooltip = ({ label, tooltip }) => (
  tooltip
    ? (
      <Tooltip
        title={tooltip}
        placement="top"
        mouseEnterDelay={0.2}
        overlayStyle={{ maxWidth: 320 }}
      >
        <span>{label}</span>
      </Tooltip>
    )
    : <span>{label}</span>
);

const isCheckFullyValidated = (summary) => {
  if (!summary) return false;
  const flagCount = summary.flagCount || 0;
  const validatedCount = summary.validatedCount || 0;
  return flagCount > 0 && validatedCount >= flagCount;
};

const hasOverrideApplied = (obj) => {
  const ao = obj?.appliedOverrides;
  return !!(ao && typeof ao === "object" && Object.keys(ao).length > 0);
};

const hasValidationSignal = (summary) => {
  if (!summary) return false;
  if ((summary.validatedCount || 0) > 0) return true;
  return hasOverrideApplied(summary);
};

const isRowClean = (row) => {
  const summaries = row.checkSummaries || {};
  return Object.values(summaries).every((s) => {
    const severity = s?.severity || "ok";
    if (severity === "ok") return true;
    return isCheckFullyValidated(s);
  });
};

const getRowStatusBucket = (row) => {
  if (isRowClean(row)) return "OK";
  const sev = row.overallSeverity || "ok";
  if (sev === "error") return "ERROR";
  if (sev === "warn") return "WARN";
  return "OK";
};

const StatusCell = ({ severity, label, validatedCount = 0, flagCount = 0, overall = false, validatedMark = false }) => {
  const fullyValidated =
    (flagCount > 0 && validatedCount >= flagCount) ||
    (validatedCount > 0 && (!severity || severity === "ok" || severity === "info"));
  if ((!severity || severity === "ok") && !fullyValidated) return null;
  const size = overall ? "large" : "default";
  const hasValidationMark = fullyValidated || validatedCount > 0 || validatedMark;
  const asterisk = hasValidationMark ? "*" : "";
  if (fullyValidated) {
    return <StatusText severity="validated" label={`VALIDATED${asterisk}`} size={size} />;
  }
  const baseLabel = severity === "info" ? "OK" : label;
  const displayLabel = `${String(baseLabel).toUpperCase()}${asterisk}`;
  const suffix = validatedCount > 0 ? ` (${validatedCount} validated)` : null;
  return <StatusText severity={severity} label={displayLabel} size={size} suffix={suffix} />;
};

const PanelDashboard = ({ idToken, getToken, userRole = null, onSelectSecurity, onApiError, initialSecurityName = null, initialBarSize = null, initialCheckName = null, activeFixJobs }) => {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialFilters = useMemo(() => readFiltersFromUrl(), []);
  const [securityType, setSecurityType] = useState(initialFilters.securityType);
  const [barSize, setBarSize] = useState(initialFilters.barSize);
  const [minSeverity, setMinSeverity] = useState(initialFilters.minSeverity);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [search, setSearch] = useState(initialFilters.search);
  const [visibility, setVisibility] = useState({});
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  const [reloadTick, setReloadTick] = useState(0);

  const emptyKeys = useMemo(() => new Set(), []);
  const runningKeys = activeFixJobs?.runningKeys || emptyKeys;
  const currentKey = activeFixJobs?.currentKey || null;
  const completionTick = activeFixJobs?.completionTick || 0;

  // When the backend moves on to a new item, the previous item's UniverseSummary
  // row has been updated in Redis — reload the dashboard so completed rows
  // display fresh severity rather than lingering on RUNNING.
  useEffect(() => {
    if (!currentKey) return;
    setReloadTick((t) => t + 1);
  }, [currentKey]);

  // When a fix job completes, refresh so overrides/severity reflect the rerun.
  useEffect(() => {
    if (completionTick === 0) return;
    setReloadTick((t) => t + 1);
  }, [completionTick]);

  useEffect(() => {
    const newSearch = buildFilterQuery({ securityType, barSize, minSeverity, statusFilter, search });
    if (window.location.search !== newSearch) {
      const target = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.history.replaceState({}, "", target);
    }
  }, [securityType, barSize, minSeverity, statusFilter, search]);

  useEffect(() => {
    const handler = () => {
      const next = readFiltersFromUrl();
      setSecurityType(next.securityType);
      setBarSize(next.barSize);
      setMinSeverity(next.minSeverity);
      setStatusFilter(next.statusFilter);
      setSearch(next.search);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [universe, catalogResponse] = await Promise.all([
          getUniverse(idToken, getToken),
          getChecksCatalog(idToken, getToken),
        ]);
        if (cancelled) return;
        setRows((universe.rows || []).map((r, i) => ({ id: i, ...r })));
        setCatalog(catalogResponse.checks || []);
      } catch (e) {
        if (!cancelled) onApiError?.(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idToken, getToken, onApiError, reloadTick]);

  useEffect(() => {
    const allKeys = [
      ...Object.keys(BASE_COLUMN_LABELS),
      ...catalog.map((c) => `check_${c.name}`),
    ];
    if (allKeys.length === 0) return;
    const defaultFor = (key) => !DEFAULT_HIDDEN_COLUMNS.has(key);
    let stored = null;
    try {
      const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
      stored = raw ? JSON.parse(raw) : null;
      if (stored && typeof stored !== "object") stored = null;
    } catch {
      stored = null;
      try { localStorage.removeItem(COLUMN_STORAGE_KEY); } catch {}
    }
    const next = {};
    for (const key of allKeys) {
      next[key] = stored && typeof stored[key] === "boolean" ? stored[key] : defaultFor(key);
    }
    setVisibility(next);
  }, [catalog]);

  const toggleColumn = useCallback((key) => {
    setVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const resetColumns = useCallback(() => {
    try { localStorage.removeItem(COLUMN_STORAGE_KEY); } catch {}
    const next = {};
    for (const key of Object.keys(BASE_COLUMN_LABELS)) next[key] = !DEFAULT_HIDDEN_COLUMNS.has(key);
    for (const c of catalog) next[`check_${c.name}`] = true;
    setVisibility(next);
  }, [catalog]);

  useEffect(() => {
    if (!initialSecurityName || rows.length === 0) return;
    const nameLower = initialSecurityName.toLowerCase();
    const barLower = initialBarSize ? initialBarSize.toLowerCase() : null;
    const match = rows.find((r) => {
      if ((r.securityName || "").toLowerCase() !== nameLower) return false;
      if (barLower && (r.barSize || "").toLowerCase() !== barLower) return false;
      return true;
    });
    if (match) {
      onSelectSecurity?.({
        securityType: match.securityType,
        securityName: match.securityName,
        barSize: match.barSize,
        initialCheckName: initialCheckName || null,
      });
    }
  }, [initialSecurityName, initialBarSize, initialCheckName, rows, onSelectSecurity]);

  const securityTypes = useMemo(() => {
    const set = new Set(rows.map((r) => r.securityType).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [rows]);

  const barSizes = useMemo(() => {
    const set = new Set(rows.map((r) => r.barSize).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [rows]);

  const rowsBeforeStatusFilter = useMemo(() => {
    const searchTerms = search
      .toLowerCase()
      .split(/[\s,]+/)
      .filter(Boolean);
    const threshold = SEVERITY_RANK[minSeverity] ?? 0;
    return rows.filter((r) => {
      if (securityType !== "All" && r.securityType !== securityType) return false;
      if (barSize !== "All" && r.barSize !== barSize) return false;
      if (searchTerms.length > 0) {
        const haystack = [
          r.securityName,
          r.bloombergTicker,
          r.description,
          r.securityType,
          r.barSize,
          r.firstDate,
          r.lastDate,
          r.overallSeverity,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!searchTerms.every((t) => haystack.includes(t))) return false;
      }
      if (minSeverity !== "All") {
        const rank = SEVERITY_RANK[r.overallSeverity] ?? 0;
        if (rank < threshold) return false;
      }
      return true;
    });
  }, [rows, securityType, barSize, minSeverity, search]);

  const statusCounts = useMemo(() => {
    const counts = { OK: 0, WARN: 0, ERROR: 0 };
    rowsBeforeStatusFilter.forEach((r) => {
      counts[getRowStatusBucket(r)] += 1;
    });
    return counts;
  }, [rowsBeforeStatusFilter]);

  const filteredRows = useMemo(() => {
    const filtered = rowsBeforeStatusFilter.filter(
      (r) => statusFilter.includes(getRowStatusBucket(r))
    );
    return [...filtered].sort((a, b) => {
      const ra = isRowClean(a) ? 0 : (SEVERITY_RANK[a.overallSeverity] ?? 0);
      const rb = isRowClean(b) ? 0 : (SEVERITY_RANK[b.overallSeverity] ?? 0);
      if (ra !== rb) return rb - ra;
      return (a.securityName || "").localeCompare(b.securityName || "");
    });
  }, [rowsBeforeStatusFilter, statusFilter]);

  const handleStatusFilterClick = useCallback((type) => {
    setStatusFilter((prev) => (
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    ));
  }, []);

  const tableColumns = useMemo(() => {
    const base = [
      {
        title: <HeaderWithTooltip label="SecurityName" tooltip={BASE_COLUMN_TOOLTIPS.securityName} />,
        dataIndex: "securityName",
        key: "securityName",
        width: 150,
        sorter: (a, b) => (a.securityName || "").localeCompare(b.securityName || ""),
        showSorterTooltip: false,
        render: (value, row) => (
          <a
            href={buildSecurityUrl(row)}
            target="_blank"
            rel="noopener noreferrer"
            className="panelDashboard__securityLink"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ),
      },
      {
        title: <HeaderWithTooltip label="Bloomberg Ticker" tooltip={BASE_COLUMN_TOOLTIPS.bloombergTicker} />,
        dataIndex: "bloombergTicker",
        key: "bloombergTicker",
        width: 160,
        sorter: (a, b) => (a.bloombergTicker || "").localeCompare(b.bloombergTicker || ""),
        showSorterTooltip: false,
      },
      {
        title: <HeaderWithTooltip label="Description" tooltip={BASE_COLUMN_TOOLTIPS.description} />,
        dataIndex: "description",
        key: "description",
        width: 220,
        sorter: (a, b) => (a.description || "").localeCompare(b.description || ""),
        showSorterTooltip: false,
      },
      {
        title: <HeaderWithTooltip label="Type" tooltip={BASE_COLUMN_TOOLTIPS.securityType} />,
        dataIndex: "securityType",
        key: "securityType",
        width: 100,
        sorter: (a, b) => (a.securityType || "").localeCompare(b.securityType || ""),
        showSorterTooltip: false,
      },
      {
        title: <HeaderWithTooltip label="BarSize" tooltip={BASE_COLUMN_TOOLTIPS.barSize} />,
        dataIndex: "barSize",
        key: "barSize",
        width: 120,
        render: (value) => formatBarSize(value),
        sorter: (a, b) => formatBarSize(a.barSize).localeCompare(formatBarSize(b.barSize)),
        showSorterTooltip: false,
      },
      {
        title: <HeaderWithTooltip label="Severity" tooltip={BASE_COLUMN_TOOLTIPS.overallSeverity} />,
        dataIndex: "overallSeverity",
        key: "overallSeverity",
        width: 140,
        sorter: (a, b) => {
          const aClean = isRowClean(a) ? 0 : (SEVERITY_RANK[a.overallSeverity] ?? 0);
          const bClean = isRowClean(b) ? 0 : (SEVERITY_RANK[b.overallSeverity] ?? 0);
          return aClean - bClean;
        },
        showSorterTooltip: false,
        render: (value, row) => {
          const rowKey = `${row.securityName}|${row.barSize}`;
          if (runningKeys.has(rowKey)) {
            const severity = currentKey === rowKey ? "running" : "queued";
            return <StatusText severity={severity} size="large" />;
          }
          const summaries = row.checkSummaries || {};
          const firstOffender = Object.entries(summaries).find(([, s]) => {
            const sev = s?.severity || "ok";
            return sev !== "ok" && !isCheckFullyValidated(s);
          })?.[0];
          const handleClick = (e) => {
            e.stopPropagation();
            const hash = firstOffender ? `#${encodeURIComponent(firstOffender)}` : "";
            window.open(`${buildSecurityUrl(row)}${hash}`, "_blank", "noopener,noreferrer");
          };
          const hasAnyValidation = Object.values(row.checkSummaries || {}).some(hasValidationSignal)
            || hasOverrideApplied(row);
          if (isRowClean(row) && (hasAnyValidation || (value && value !== "ok"))) {
            return (
              <span className="panelDashboard__statusLink" onClick={handleClick}>
                <span className="statusText statusText--ok statusText--overall">OK*</span>
              </span>
            );
          }
          const severity = value || "ok";
          if (severity === "ok") {
            const okLabel = hasAnyValidation ? "OK*" : "OK";
            return <span className="statusText statusText--ok statusText--overall">{okLabel}</span>;
          }
          const cell = <StatusCell severity={value} label={value} overall validatedMark={hasAnyValidation} />;
          if (!cell) return null;
          return (
            <span className="panelDashboard__statusLink" onClick={handleClick}>
              {cell}
            </span>
          );
        },
      },
      {
        title: <HeaderWithTooltip label="FirstDate" tooltip={BASE_COLUMN_TOOLTIPS.firstDate} />,
        dataIndex: "firstDate",
        key: "firstDate",
        width: 120,
        sorter: (a, b) => (a.firstDate || "").localeCompare(b.firstDate || ""),
        showSorterTooltip: false,
        render: (v) => (v ? String(v).slice(0, 10) : ""),
      },
      {
        title: <HeaderWithTooltip label="LastDate" tooltip={BASE_COLUMN_TOOLTIPS.lastDate} />,
        dataIndex: "lastDate",
        key: "lastDate",
        width: 120,
        sorter: (a, b) => (a.lastDate || "").localeCompare(b.lastDate || ""),
        showSorterTooltip: false,
        render: (v) => (v ? String(v).slice(0, 10) : ""),
      },
    ];
    const daysSinceCheck = catalog.find((c) => c.name === DAYS_SINCE_NAME);
    const daysSinceColumn = daysSinceCheck
      ? {
          title: (
            <HeaderWithTooltip
              label={daysSinceCheck.displayName || DAYS_SINCE_NAME}
              tooltip={daysSinceCheck.description}
            />
          ),
          key: `check_${DAYS_SINCE_NAME}`,
          width: 140,
          sorter: (a, b) => {
            const sa = (a.checkSummaries || {})[DAYS_SINCE_NAME] || {};
            const sb = (b.checkSummaries || {})[DAYS_SINCE_NAME] || {};
            const da = typeof sa.daysSince === "number" ? sa.daysSince : -1;
            const db = typeof sb.daysSince === "number" ? sb.daysSince : -1;
            return da - db;
          },
          showSorterTooltip: false,
          render: (_, row) => {
            const rowKey = `${row.securityName}|${row.barSize}`;
            if (runningKeys.has(rowKey)) {
              const severity = currentKey === rowKey ? "running" : "queued";
              return <StatusText severity={severity} />;
            }
            if (
              daysSinceCheck.appliesToBarSizes &&
              !daysSinceCheck.appliesToBarSizes.includes(row.barSize)
            ) {
              return <span className="statusText statusText--na">N/A</span>;
            }
            const summary = (row.checkSummaries || {})[DAYS_SINCE_NAME] || {};
            const daysSince = summary.daysSince;
            if (daysSince === null || daysSince === undefined) {
              return <span className="statusText statusText--na">—</span>;
            }
            const severity = summary.severity || "ok";
            const label = `${daysSince}`;
            return (
              <span
                className="panelDashboard__statusLink"
                onClick={(e) => {
                  e.stopPropagation();
                  const hash = `#${encodeURIComponent(DAYS_SINCE_NAME)}`;
                  window.open(`${buildSecurityUrl(row)}${hash}`, "_blank", "noopener,noreferrer");
                }}
              >
                <span className={`statusText statusText--${severity}`}>{label}</span>
              </span>
            );
          },
        }
      : null;
    if (daysSinceColumn) {
      // Insert immediately after the Severity column, before FirstDate / LastDate.
      const severityIndex = base.findIndex((c) => c.key === "overallSeverity");
      const insertAt = severityIndex >= 0 ? severityIndex + 1 : base.length;
      base.splice(insertAt, 0, daysSinceColumn);
    }
    const checkCols = catalog
      .filter((c) => c.name !== DAYS_SINCE_NAME)
      .map((check) => {
      const isApplicable = (row) => {
        if (check.appliesToSecurityTypes && !check.appliesToSecurityTypes.includes(row.securityType)) return false;
        if (check.appliesToBarSizes && !check.appliesToBarSizes.includes(row.barSize)) return false;
        return true;
      };
      return {
        title: (
          <HeaderWithTooltip
            label={check.displayName || check.name}
            tooltip={check.description}
          />
        ),
        key: `check_${check.name}`,
        width: 160,
        sorter: (a, b) => {
          const sa = (a.checkSummaries || {})[check.name] || {};
          const sb = (b.checkSummaries || {})[check.name] || {};
          const ra = isCheckFullyValidated(sa) ? 0 : (SEVERITY_RANK[sa.severity] ?? 0);
          const rb = isCheckFullyValidated(sb) ? 0 : (SEVERITY_RANK[sb.severity] ?? 0);
          return ra - rb;
        },
        showSorterTooltip: false,
        render: (_, row) => {
          const rowKey = `${row.securityName}|${row.barSize}`;
          if (runningKeys.has(rowKey)) {
            const severity = currentKey === rowKey ? "running" : "queued";
            return <StatusText severity={severity} />;
          }
          if (!isApplicable(row)) {
            return <span className="statusText statusText--na">N/A</span>;
          }
          const summaries = row.checkSummaries || {};
          if (!(check.name in summaries)) return null;
          const summary = summaries[check.name] || {};
          const severity = summary.severity || "ok";
          const validatedCount = summary.validatedCount || 0;
          const flagCount = summary.flagCount || 0;
          const checkSignal = hasValidationSignal(summary);
          const fullyValidated =
            (flagCount > 0 && validatedCount >= flagCount) ||
            (validatedCount > 0 && (severity === "ok" || severity === "info"));
          if (severity === "ok" && !fullyValidated) {
            const okLabel = checkSignal ? "OK*" : "OK";
            return <span className="statusText statusText--ok">{okLabel}</span>;
          }
          const cell = (
            <StatusCell
              severity={severity}
              label={severity}
              validatedCount={validatedCount}
              flagCount={flagCount}
              validatedMark={checkSignal}
            />
          );
          if (!cell) return null;
          return (
            <span
              className="panelDashboard__statusLink"
              onClick={(e) => {
                e.stopPropagation();
                const hash = `#${encodeURIComponent(check.name)}`;
                window.open(`${buildSecurityUrl(row)}${hash}`, "_blank", "noopener,noreferrer");
              }}
            >
              {cell}
            </span>
          );
        },
      };
    });
    return [...base, ...checkCols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, runningKeys, currentKey]);

  const visibleColumns = useMemo(
    () => tableColumns.filter((c) => visibility[c.key] !== false),
    [tableColumns, visibility],
  );

  const columnMenuEntries = useMemo(() => {
    const base = Object.entries(BASE_COLUMN_LABELS).map(([key, label]) => ({ key, label }));
    const checks = catalog.map((c) => ({
      key: `check_${c.name}`,
      label: c.displayName || c.name,
    }));
    return [...base, ...checks];
  }, [catalog]);

  const handleRow = (record) => ({
    onClick: () => {
      window.open(buildSecurityUrl(record), "_blank", "noopener,noreferrer");
    },
    onAuxClick: (e) => {
      if (e.button === 1) {
        e.preventDefault();
        window.open(buildSecurityUrl(record), "_blank", "noopener,noreferrer");
      }
    },
  });

  const scrollX = useMemo(
    () => visibleColumns.reduce((sum, c) => sum + (c.width || 0), 0),
    [visibleColumns]
  );

  const selectMenuProps = {
    PaperProps: { className: "panelDashboard__selectMenu" },
  };

  return (
    <Box className="panelDashboard">
      <Box className="panelDashboard__header">
        <Box className="panelDashboard__filters">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            className="panelDashboard__columnsButton"
            onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
          >
            Columns
          </Button>
          <Select
            size="small"
            value={securityType}
            onChange={(e) => setSecurityType(e.target.value)}
            className="panelDashboard__filter"
            MenuProps={selectMenuProps}
          >
            {securityTypes.map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
          </Select>
          <Select
            size="small"
            value={barSize}
            onChange={(e) => setBarSize(e.target.value)}
            className="panelDashboard__filter"
            MenuProps={selectMenuProps}
          >
            {barSizes.map((t) => (<MenuItem key={t} value={t}>{t === "All" ? t : formatBarSize(t)}</MenuItem>))}
          </Select>
          <Select
            size="small"
            value={minSeverity}
            onChange={(e) => setMinSeverity(e.target.value)}
            className="panelDashboard__filter"
            MenuProps={selectMenuProps}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="warn">Warning</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
          <TextField
            size="small"
            placeholder="Filter by name, ticker, etc."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="panelDashboard__searchField"
          />
          <Box className="panelDashboard__statusFilters">
            {STATUS_FILTER_TYPES.map((type) => {
              const isSelected = statusFilter.includes(type);
              const className = [
                "panelDashboard__statusFilter",
                isSelected ? "panelDashboard__statusFilter--selected" : "",
              ].filter(Boolean).join(" ");
              return (
                <span
                  key={type}
                  className={className}
                  style={{ color: STATUS_FILTER_COLORS[type] }}
                  onClick={() => handleStatusFilterClick(type)}
                >
                  {type}({statusCounts[type]})
                </span>
              );
            })}
          </Box>
          <Menu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={() => setColumnMenuAnchor(null)}
            slotProps={{ paper: { className: "panelDashboard__columnsMenu" } }}
          >
            {columnMenuEntries.map((entry) => (
              <MenuItem
                key={entry.key}
                dense
                onClick={() => toggleColumn(entry.key)}
              >
                <Checkbox
                  size="small"
                  checked={visibility[entry.key] !== false}
                  className="panelDashboard__columnsCheckbox"
                />
                <ListItemText primary={entry.label} />
              </MenuItem>
            ))}
            <MenuItem
              dense
              className="panelDashboard__columnsReset"
              onClick={() => { resetColumns(); setColumnMenuAnchor(null); }}
            >
              <ListItemText primary="Reset to defaults" />
            </MenuItem>
          </Menu>
          <Box className="panelDashboard__counter">
            {filteredRows.length} / {rows.length} selected
          </Box>
        </Box>
      </Box>
      <Box className="panelDashboard__grid">
        <ConfigProvider
          theme={{
            algorithm: antdTheme.darkAlgorithm,
            token: { colorPrimary: "#78bcfb" },
          }}
        >
          <Table
            className="panelDashboard__table"
            columns={visibleColumns}
            dataSource={filteredRows}
            rowKey="id"
            loading={loading}
            pagination={false}
            size="middle"
            scroll={{ x: scrollX }}
            onRow={handleRow}
            rowClassName="panelDashboard__row"
          />
        </ConfigProvider>
      </Box>
    </Box>
  );
};

export default PanelDashboard;
